import { useEffect, useMemo, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const PAD = 8;

const COPY = {
  en: {
    steps: [
      { target: "start-working", title: "Start here", body: "These are your fastest ways into today's work: intake, available leads, a new lead, or your follow-up queue." },
      { target: "work-today-grid", title: "Work the priorities first", body: "My Work Today surfaces what needs attention now. Each tile opens the exact work behind that number." },
      { target: "pipeline-flow", title: "See where every lead stands", body: "Your pipeline shows the active stages in your book. Use it to spot stalled or urgent business and move it forward." },
      { target: "tools-hub", title: "Your carriers and tools, secured", body: "This hub shows which carrier and work systems are ready without exposing passwords. Open the Vault when you need the protected login workflow." },
      { target: "production-panel", title: "Know what is producing and what is paid", body: "Production and commissions stay separate so you can distinguish expected business, received money, carrier payments pending, and first-premium risk." },
      { target: "copilot-open", title: "Fortunate Copilot stays with you", body: "Use Fortunate Copilot from any screen for page guidance, case help, drafting, and next-action support. It should guide you through the product, not make you memorize it." },
    ],
    step: "Step",
    of: "of",
    skip: "Skip",
    back: "Back",
    next: "Next",
    finish: "Start working",
    replay: "Take the tour",
  },
  es: {
    steps: [
      { target: "start-working", title: "Empieza aquí", body: "Estas son las rutas más rápidas al trabajo de hoy: admisión, prospectos disponibles, un prospecto nuevo o tu cola de seguimientos." },
      { target: "work-today-grid", title: "Trabaja primero las prioridades", body: "Mi Trabajo de Hoy muestra lo que requiere atención ahora. Cada tarjeta abre directamente el trabajo detrás de ese número." },
      { target: "pipeline-flow", title: "Mira en qué etapa está cada prospecto", body: "Tu embudo muestra las etapas activas de tu cartera. Úsalo para detectar negocio detenido o urgente y hacerlo avanzar." },
      { target: "tools-hub", title: "Tus aseguradoras y herramientas, protegidas", body: "Este centro muestra qué sistemas están listos sin exponer contraseñas. Abre la Bóveda cuando necesites el flujo protegido de acceso." },
      { target: "production-panel", title: "Distingue producción de dinero recibido", body: "Producción y comisiones permanecen separadas para distinguir negocio esperado, dinero recibido, pagos pendientes de la aseguradora y riesgo de primera prima." },
      { target: "copilot-open", title: "Fortunate Copilot te acompaña", body: "Usa Fortunate Copilot desde cualquier pantalla para orientación, ayuda con casos, redacción y próximos pasos. Debe guiarte por el producto, no hacerte memorizarlo." },
    ],
    step: "Paso",
    of: "de",
    skip: "Omitir",
    back: "Atrás",
    next: "Siguiente",
    finish: "Empezar a trabajar",
    replay: "Ver recorrido",
  },
};

function targetRect(testid, onRect) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) { onRect(null); return false; }
  el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  window.setTimeout(() => onRect(el.getBoundingClientRect()), 260);
  return true;
}

export function FirstLoginTour({ storageKey = "fo_tour_agent", auto = true }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const copy = COPY[lang];
  const steps = useMemo(() => copy.steps, [copy]);
  const [open, setOpen] = useState(() => auto && !localStorage.getItem(storageKey));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!open) return;
    targetRect(steps[i]?.target, setRect);
  }, [open, steps, i]);

  useEffect(() => {
    const launch = () => { localStorage.removeItem(storageKey); setI(0); setOpen(true); };
    window.addEventListener("fortunate:start-agent-tour", launch);
    return () => window.removeEventListener("fortunate:start-agent-tour", launch);
  }, [storageKey]);

  useEffect(() => {
    if (!open) return;
    setRect(null);
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (targetRect(steps[i]?.target, setRect) || tries > 12) window.clearInterval(timer);
    }, 160);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, i, steps, measure]);

  const close = () => { localStorage.setItem(storageKey, "1"); setOpen(false); setI(0); setRect(null); };
  if (!open) return null;

  const step = steps[i];
  const last = i === steps.length - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(360, vw - 24);
  const cardH = 230;

  const cut = rect ? {
    position: "fixed", top: Math.max(4, rect.top - PAD), left: Math.max(4, rect.left - PAD),
    width: Math.min(vw - 8, rect.width + PAD * 2), height: Math.min(vh - 8, rect.height + PAD * 2),
    borderRadius: 12, boxShadow: "0 0 0 9999px rgba(13,31,48,0.68)", outline: "2px solid #B28A3D",
    zIndex: 100, pointerEvents: "none", transition: "all .2s ease",
  } : null;

  let cardStyle = { width: cardW, maxWidth: "calc(100vw - 24px)" };
  if (!rect) {
    cardStyle = { ...cardStyle, top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  } else if (vw < 640) {
    const topHalf = rect.top + rect.height / 2 < vh / 2;
    cardStyle = topHalf
      ? { ...cardStyle, left: 12, right: 12, width: "auto", bottom: 16 }
      : { ...cardStyle, left: 12, right: 12, width: "auto", top: 16 };
  } else {
    const left = Math.max(16, Math.min(rect.left, vw - cardW - 16));
    if (vh - rect.bottom > cardH + 20) cardStyle = { ...cardStyle, top: rect.bottom + 14, left };
    else if (rect.top > cardH + 20) cardStyle = { ...cardStyle, top: rect.top - cardH - 14, left };
    else cardStyle = { ...cardStyle, top: 18, left: Math.max(16, vw - cardW - 18) };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="first-login-tour" aria-live="polite">
      {rect ? <div style={cut} data-testid="first-login-tour-spotlight" /> : <div className="absolute inset-0 bg-[#0d1f30]/70" />}
      <div style={{ position: "fixed", zIndex: 101, ...cardStyle }} className="rounded-2xl border border-[#B28A3D]/40 bg-card p-5 shadow-2xl">
        <button onClick={close} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground" data-testid="tour-close"><X className="h-5 w-5" /></button>
        <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#17324D] text-[#B28A3D]"><Compass className="h-5 w-5" /></div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{copy.step} {i + 1} {copy.of} {steps.length}</div>
        <h3 className="mt-1 pr-6 font-heading text-xl font-bold">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex gap-1">
            {steps.map((_, k) => <span key={k} className={`h-1.5 w-5 rounded-full ${k <= i ? "bg-[#B28A3D]" : "bg-border"}`} />)}
          </div>
          <div className="flex-1" />
          {!last && <Button variant="ghost" size="sm" onClick={close} data-testid="tour-skip-btn">{copy.skip}</Button>}
          {i > 0 && <Button variant="outline" size="sm" onClick={() => setI((x) => Math.max(0, x - 1))} data-testid="tour-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> {copy.back}</Button>}
          <Button size="sm" onClick={() => (last ? close() : setI((x) => x + 1))} data-testid="tour-next-btn">
            {last ? copy.finish : copy.next} {!last && <ArrowRight className="ml-1 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TourReplayButton({ storageKey = "fo_tour_agent", onReplay }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  return (
    <Button variant="outline" size="sm" data-testid="tour-replay-btn"
      onClick={() => {
        localStorage.removeItem(storageKey);
        window.dispatchEvent(new CustomEvent("fortunate:start-agent-tour"));
        onReplay?.();
      }}>
      <Compass className="mr-1.5 h-4 w-4" /> {COPY[lang].replay}
    </Button>
  );
}
